import { createElement } from "react";
import { openExternalUrl } from "@/lib/open-url";

interface UserInfo {
  id: string;
  display_name: string;
}

interface ChannelInfo {
  id: string;
  name: string;
}

/**
 * Extract user IDs mentioned via @displayname in content.
 */
export function extractMentionIds(
  content: string,
  users: UserInfo[],
): string[] {
  const ids: string[] = [];
  for (const user of users) {
    // Use word boundary after the display name to avoid partial matches
    // e.g. @Al should not match @Alice
    const escaped = user.display_name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`@${escaped}(?!\\w)`);
    if (regex.test(content)) {
      ids.push(user.id);
    }
  }
  return ids;
}

/**
 * Format inline markdown: **bold**, *italic*, `code`, and links.
 */
function formatInline(
  text: string,
  keyPrefix: string,
): (string | React.ReactElement)[] {
  // Match **bold**, *italic*, `code`, ::highlight::, [text](url) markdown links, bare URLs, and bare domains
  const regex =
    /(\*\*(.+?)\*\*|\*(.+?)\*|`(.+?)`|::(.+?)::|(\[([^\]]+)\]\((https?:\/\/[^)]+)\))|(https?:\/\/[^\s<]+)|(?<![/@\w.-])((?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?:\/[^\s<]*)?))/gi;
  const result: (string | React.ReactElement)[] = [];
  let lastIndex = 0;
  let match;
  let idx = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      result.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      // **bold** — recursively parse inner content
      const inner = formatInline(match[2], `${keyPrefix}-b${idx}`);
      result.push(
        createElement(
          "strong",
          { key: `${keyPrefix}-b${idx}`, className: "font-bold" },
          ...inner,
        ),
      );
    } else if (match[3]) {
      // *italic* — recursively parse inner content
      const inner = formatInline(match[3], `${keyPrefix}-i${idx}`);
      result.push(
        createElement(
          "em",
          { key: `${keyPrefix}-i${idx}`, className: "italic" },
          ...inner,
        ),
      );
    } else if (match[4]) {
      // `code`
      result.push(
        createElement(
          "code",
          {
            key: `${keyPrefix}-c${idx}`,
            className: "text-info font-mono text-xs",
          },
          match[4],
        ),
      );
    } else if (match[5]) {
      // ::highlight:: — bright purple
      result.push(
        createElement(
          "span",
          {
            key: `${keyPrefix}-h${idx}`,
            className: "text-primary font-medium",
          },
          match[5],
        ),
      );
    } else if (match[7] && match[8]) {
      // [text](url) markdown link
      const linkText = match[7];
      const linkUrl = match[8];
      result.push(
        createElement(
          "a",
          {
            key: `${keyPrefix}-ml${idx}`,
            href: linkUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "text-info hover:underline cursor-pointer",
            onClick: (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              void openExternalUrl(linkUrl);
            },
          },
          linkText,
        ),
      );
    } else if (match[9]) {
      // Bare URL — strip trailing punctuation with unbalanced parens
      let url = match[9];
      while (url.length > 0) {
        const last = url[url.length - 1];
        if (".,;:!?\"'".includes(last)) {
          url = url.slice(0, -1);
        } else if (last === ")") {
          const opens = (url.match(/\(/g) || []).length;
          const closes = (url.match(/\)/g) || []).length;
          if (closes > opens) {
            url = url.slice(0, -1);
          } else {
            break;
          }
        } else {
          break;
        }
      }
      const trimmed = match[9].length - url.length;
      if (trimmed > 0) {
        lastIndex = match.index + match[0].length - trimmed;
        regex.lastIndex = lastIndex;
      }
      result.push(
        createElement(
          "a",
          {
            key: `${keyPrefix}-l${idx}`,
            href: url,
            target: "_blank",
            rel: "noopener noreferrer",
            className: "text-info hover:underline cursor-pointer",
            onClick: (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              void openExternalUrl(url);
            },
          },
          url,
        ),
      );
    } else if (match[10]) {
      // Bare domain — prepend https:// and add tooltip
      let domain = match[10];
      // Strip trailing punctuation (same logic as bare URLs)
      while (domain.length > 0) {
        const last = domain[domain.length - 1];
        if (".,;:!?\"'".includes(last)) {
          domain = domain.slice(0, -1);
        } else if (last === ")") {
          const opens = (domain.match(/\(/g) || []).length;
          const closes = (domain.match(/\)/g) || []).length;
          if (closes > opens) domain = domain.slice(0, -1);
          else break;
        } else break;
      }
      const trimmed = match[10].length - domain.length;
      if (trimmed > 0) {
        lastIndex = match.index + match[0].length - trimmed;
        regex.lastIndex = lastIndex;
      }
      const fullUrl = "https://" + domain;
      result.push(
        createElement(
          "a",
          {
            key: `${keyPrefix}-bd${idx}`,
            href: fullUrl,
            target: "_blank",
            rel: "noopener noreferrer",
            title: fullUrl,
            className: "text-info hover:underline cursor-pointer",
            onClick: (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              void openExternalUrl(fullUrl);
            },
          },
          domain,
        ),
      );
    }
    lastIndex = match.index + match[0].length;
    idx++;
  }
  if (lastIndex < text.length) {
    result.push(text.slice(lastIndex));
  }
  return result.length > 0 ? result : [text];
}

/**
 * Render message content with mentions highlighted and basic formatting.
 */
export function renderMentions(
  content: string,
  users: UserInfo[],
  onMentionClick?: (userId: string) => void,
  channels?: ChannelInfo[],
  onChannelClick?: (channelId: string) => void,
): (string | React.ReactElement)[] {
  const hasUsers = users.length > 0;
  const hasChannels = (channels?.length ?? 0) > 0;
  if (!hasUsers && !hasChannels) return formatInline(content, "root");

  // Sort users by name length descending to match longest first
  const sortedUsers = [...users].sort(
    (a, b) => b.display_name.length - a.display_name.length,
  );
  const sortedChannels = channels
    ? [...channels].sort((a, b) => b.name.length - a.name.length)
    : [];

  const patterns: string[] = [];
  if (hasUsers) {
    patterns.push(...sortedUsers.map((u) => `@${escapeRegex(u.display_name)}`));
  }
  if (hasChannels) {
    patterns.push(
      ...sortedChannels.map((ch) => `(?<=^|\\s)#${escapeRegex(ch.name)}`),
    );
  }
  const regex = new RegExp(`(${patterns.join("|")})`, "g");
  const parts = content.split(regex);

  const result: (string | React.ReactElement)[] = [];
  parts.forEach((part, i) => {
    if (part.startsWith("@")) {
      const name = part.slice(1);
      const matchedUser = users.find((u) => u.display_name === name);
      if (matchedUser) {
        result.push(
          createElement(
            "span",
            {
              key: i,
              className: `text-warning rounded px-0.5 font-medium${onMentionClick ? " cursor-pointer hover:underline" : ""}`,
              ...(onMentionClick && {
                role: "button",
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onMentionClick(matchedUser.id);
                },
              }),
            },
            part,
          ),
        );
        return;
      }
    }
    if (part.startsWith("#") && sortedChannels.length > 0) {
      const chName = part.slice(1);
      const matchedChannel = sortedChannels.find((ch) => ch.name === chName);
      if (matchedChannel) {
        result.push(
          createElement(
            "span",
            {
              key: i,
              className: `text-info rounded px-0.5 font-medium${onChannelClick ? " cursor-pointer hover:underline" : ""}`,
              ...(onChannelClick && {
                role: "button",
                onClick: (e: React.MouseEvent) => {
                  e.stopPropagation();
                  onChannelClick(matchedChannel.id);
                },
              }),
            },
            part,
          ),
        );
        return;
      }
    }
    result.push(...formatInline(part, `p${i}`));
  });
  return result;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
